import { useState } from "react";

type UseTableSortParams<T> = {
  initialOrderBy: T;
  initialOrder?: "asc" | "desc";
};

/**
 * Custom React hook to manage sorting state for tables.
 *
 * @template T - The type of the column identifier.
 * @param params - Parameters for initializing the sort state.
 * @param params.initialOrderBy - The initial column to sort by.
 * @param params.initialOrder - The initial sort order ("asc" or "desc"). Defaults to "asc" if not provided.
 * @returns An object containing the current sort state (`tableSort`)
 * and a handler (`handleRequestSort`) to update the sort state.
 *
 * @example
 * const { tableSort, handleRequestSort } = useTableSort({
 *   initialOrderBy: 'name',
 *   initialOrder: 'asc',
 * });
 */
export function useTableSort<T>({
  initialOrderBy,
  initialOrder,
}: UseTableSortParams<T>) {
  const [tableSort, setTableSort] = useState<{
    orderBy: T;
    order: "asc" | "desc";
  }>({
    orderBy: initialOrderBy,
    order: initialOrder ?? "asc",
  });

  const handleRequestSort = (id: T) => {
    const isAsc = tableSort.orderBy === id && tableSort.order === "asc";
    setTableSort({ orderBy: id, order: isAsc ? "desc" : "asc" });
  };

  return { handleRequestSort, tableSort };
}
